/* ============================================================
   Slope filter — client-side show/hide of collection cards.

   Reads each card's data-slope-ranges attribute (space-separated
   canonical tags) written by snippets/slope-badges.liquid. The
   tag -> shape map and angle-bucket overlap rules below MUST stay
   identical to snippets/slope-badges.liquid.
   ============================================================ */
(function () {
  'use strict';

  // tag -> shape. Mirrors the Liquid badge table.
  var SHAPE_OF = {
    '10-24': 'linear',
    '10-30': 'curved',
    '25-30': 'linear',
    '31-35': 'linear',
    '36-45': 'linear',
    '31-45': 'curved'
  };

  // Does a single tag satisfy the selected Slope Type? The 25-45 tag has no
  // shape (degrees-only badge), so it's absent from SHAPE_OF and only shows
  // when Type = All.
  function shapeMatches(tag, type) {
    if (type === 'all') return true;
    return SHAPE_OF[tag] === type;
  }

  // Does a single tag fall in the selected angle bucket? Buckets overlap:
  // 10-30 (curved) belongs to BOTH the 10-24 and 25-30 buckets.
  // 25-45 (25°–45°) spans BOTH the 25-30 and 31-45 buckets.
  function angleMatches(tag, bucket) {
    if (bucket === 'all') return true;
    if (bucket === '10-24') return tag === '10-24' || tag === '10-30';
    if (bucket === '25-30') return tag === '25-30' || tag === '10-30' || tag === '25-45';
    if (bucket === '31-45') return tag === '31-35' || tag === '36-45' || tag === '31-45' || tag === '25-45';
    return false;
  }

  function initFilter(bar) {
    if (!bar || bar.dataset.slopeInit === '1') return;
    bar.dataset.slopeInit = '1';

    // Scope every query to this collection wrapper so multiple grids
    // on one page never interfere with each other.
    var scope = bar.closest('[data-collection-id]') || bar.parentElement;
    if (!scope) return;
    var grid = scope.querySelector('.collection-products');
    if (!grid) return;

    var state = { type: 'all', angle: 'all' };

    var typeChips = bar.querySelectorAll('[data-slope-type]');
    var angleChips = bar.querySelectorAll('[data-slope-angle]');
    var clearBtn = bar.querySelector('[data-slope-clear]');
    var countEl = bar.querySelector('[data-slope-count]');
    var dotEl = bar.querySelector('[data-slope-dot]');
    var emptyEl = scope.querySelector('[data-slope-empty]');

    function cardVisible(card) {
      var raw = (card.getAttribute('data-slope-ranges') || '').trim();
      if (!raw) {
        // Untagged cards show only when no filter is active.
        return state.type === 'all' && state.angle === 'all';
      }
      var tags = raw.split(/\s+/);
      for (var i = 0; i < tags.length; i++) {
        // The AND is evaluated PER TAG: a single tag must satisfy both axes.
        if (shapeMatches(tags[i], state.type) && angleMatches(tags[i], state.angle)) {
          return true;
        }
      }
      return false;
    }

    function setActive(chips, attr, value) {
      Array.prototype.forEach.call(chips, function (chip) {
        var on = chip.getAttribute(attr) === value;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function apply() {
      var cards = grid.querySelectorAll('.collection-product');
      var visible = 0;
      Array.prototype.forEach.call(cards, function (card) {
        if (cardVisible(card)) {
          card.classList.remove('slope-hidden');
          visible += 1;
        } else {
          card.classList.add('slope-hidden');
        }
      });

      if (emptyEl) emptyEl.hidden = visible !== 0;

      var active = state.type !== 'all' || state.angle !== 'all';
      if (countEl) {
        countEl.innerHTML = active
          ? '<strong>' + visible + '</strong> ' + (visible === 1 ? 'product' : 'products')
          : '';
      }
      if (dotEl) dotEl.hidden = !active;
      if (clearBtn) clearBtn.hidden = !active;
    }

    // After a selection, bring the top of the filtered products into view.
    // Anchor on the (non-sticky) product grid — the sticky filter bar's own
    // offsets report its pinned position, not its layout position — and
    // subtract the sticky chrome stacked above the grid: the fixed header,
    // the sticky category nav, and the pinned filter bar (plus its margin).
    function scrollToProductsTop() {
      var headerH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-top'), 10
      ) || 60;

      var catNavH = 0;
      var catNav = document.querySelector('.sticky-navigation');
      if (catNav) {
        var catSection = catNav.closest('[id^="shopify-section-"]') || catNav;
        catNavH = catSection.getBoundingClientRect().height;
      }

      var barSpace = bar.getBoundingClientRect().height + 24; // + margin-bottom
      var gridTop = grid.getBoundingClientRect().top + window.pageYOffset;
      var target = gridTop - headerH - catNavH - barSpace;
      window.scrollTo({ top: target < 0 ? 0 : target, behavior: 'smooth' });
    }

    // Single source of truth for choosing a Slope Type: updates state,
    // reflects the selected chip, and re-filters. Both the type chips and
    // the help-popover illustration cards go through this.
    function selectType(value) {
      state.type = value;
      setActive(typeChips, 'data-slope-type', state.type);
      apply();
      scrollToProductsTop();
    }

    Array.prototype.forEach.call(typeChips, function (chip) {
      chip.addEventListener('click', function () {
        selectType(chip.getAttribute('data-slope-type'));
      });
    });

    Array.prototype.forEach.call(angleChips, function (chip) {
      chip.addEventListener('click', function () {
        state.angle = chip.getAttribute('data-slope-angle');
        setActive(angleChips, 'data-slope-angle', state.angle);
        apply();
        scrollToProductsTop();
      });
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        state.type = 'all';
        state.angle = 'all';
        setActive(typeChips, 'data-slope-type', 'all');
        setActive(angleChips, 'data-slope-angle', 'all');
        apply();
      });
    }

    initHelpPopover(bar, selectType);

    apply();
  }

  // Help (?) popover explaining linear vs. curved slopes. Toggled by its
  // button and closed via the × button, an outside click, or the Esc key.
  // Its illustration cards select a Slope Type through the passed-in
  // onSelectType callback (the same path the type chips use), then close.
  // Scoped to a single filter bar.
  function initHelpPopover(bar, onSelectType) {
    var helpBtn = bar.querySelector('[data-slope-help-btn]');
    var popover = bar.querySelector('[data-slope-help-popover]');
    if (!helpBtn || !popover) return;

    var closeBtn = popover.querySelector('[data-slope-help-close]');
    var optionCards = popover.querySelectorAll('[data-slope-select-type]');

    function isOpen() {
      return !popover.hidden;
    }

    function open() {
      popover.hidden = false;
      helpBtn.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onOutsideClick, true);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      popover.hidden = true;
      helpBtn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onOutsideClick, true);
      document.removeEventListener('keydown', onKeydown);
    }

    function onOutsideClick(event) {
      if (popover.contains(event.target) || helpBtn.contains(event.target)) return;
      close();
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        close();
        helpBtn.focus();
      }
    }

    helpBtn.addEventListener('click', function (event) {
      event.stopPropagation();
      if (isOpen()) {
        close();
      } else {
        open();
      }
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        close();
        helpBtn.focus();
      });
    }

    Array.prototype.forEach.call(optionCards, function (card) {
      card.addEventListener('click', function () {
        if (typeof onSelectType === 'function') {
          onSelectType(card.getAttribute('data-slope-select-type'));
        }
        close();
        helpBtn.focus();
      });
    });
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var bars = scope.querySelectorAll('[data-slope-filter]');
    Array.prototype.forEach.call(bars, initFilter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-initialize inside the theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });
})();
